import * as React from 'react';
import { FieldProps } from '../metadataFormField';
import { FieldGroup } from '../FieldGroup/fieldGroup';
import { TreeSelect, traverse } from '../../TreeSelect/treeSelect';
import { DataSourceModal } from './dataSourceModal';
import { Icon } from 'rcd-cpp-ui/ui/icon/Icon';
import { Close } from 'rcd-cpp-ui/ui/icon/Remove';
import { Metadata } from '../../../types';
import { Spinner } from 'rcd-cpp-ui/ui/loader';
import { useSelector } from '../../../utils/useSelector';
import { NodeType } from '../../../enums/NodeType';
import { ValidationResponse } from '../../../types/ValidationResponse';
import { getItemStatusId, isRemovedOrRetired } from '../../../utils';
import { ItemStatus } from '../../../enums/ItemStatus';
import { DataSource } from '../../../enums/DataSource';
import { FieldType } from '../../../enums/FieldType';

export const dataSourceItemField = {
  field: 'dataSourceItem',
  key: 'Item.dataSourceItem',
  fieldType: FieldType.reference
};

export const DataSourceField = (props: FieldProps) => {
  const { data, updater } = props;
  const { updateFields, update } = updater;
  const [showModal, setShowModal] = React.useState(false);
  const schema = useSelector(s => s.form?.schema);
  const fields = schema?.props?.fields;
  const dataSources = useSelector(s => s.form?.data?.props?.dataSource);
  const projects = useSelector(s => s.form?.data?.props?.project);
  const dataSourceItems = useSelector(s => s.form?.data?.props?.dataSourceItem);
  const dataSourceField = !!fields?.filter(f => f?.key == 'Item.dataSource')
    ?.length
    ? fields?.filter(f => f?.key == 'Item.dataSource')[0]
    : null;
  const projectField = !!fields?.filter(f => f?.key == 'Item.project')?.length
    ? fields?.filter(f => f?.key == 'Item.project')[0]
    : null;
  const disabled = props.disabled || isDisabled(data);

  const handleRemove = async value => {
    const isDataSource = value?.labels?.includes(NodeType.DataSource);
    const isItem = value?.labels?.includes(NodeType.Item);
    const updatedDataSources = dataSources?.filter(
      ds => ds.props?.refId !== value.props?.refId
    );
    const updatedProjects = projects?.filter(project => {
      return isDataSource
        ? value.props?.refId !== 'ds:' + project.props?.dataSourceId
        : project.props?.refId !== value.props?.refId;
    });
    let updatedItems = dataSourceItems;
    if (
      isDataSource &&
      value.props?.id == DataSource.PublishedGartnerResearch
    ) {
      updatedItems = [];
    } else if (isItem) {
      updatedItems = dataSourceItems?.filter(
        i => i?.props?.refId != value.props?.refId
      );
    }

    updateFields([
      { field: dataSourceField, value: updatedDataSources },
      { field: projectField, value: updatedProjects },
      { field: dataSourceItemField, value: updatedItems }
    ]);

    await update();
  };

  const saveDsAndProjects = async (
    dataSourceList: Metadata[],
    projectList: Metadata[],
    dataSourceItemList: Metadata[]
  ) => {
    updateFields([
      { field: dataSourceField, value: dataSourceList },
      { field: projectField, value: projectList },
      { field: dataSourceItemField, value: dataSourceItemList }
    ]);

    await update();
  };

  const childValues = React.useMemo(() => {
    const vals = [];
    projects?.forEach(p => {
      vals.push(p);
    });
    dataSourceItems?.forEach(i => {
      if (i?.props) {
        vals.push({
          labels: [NodeType.Item],
          props: {
            ...i.props,
            name: i.props.title,
            projectId: i.props.itemId,
            externalId: i.props.itemId,
            dataSourceId: DataSource.PublishedGartnerResearch
          }
        });
      }
    });
    return vals;
  }, [projects, dataSourceItems]);
  return (
    <FieldGroup
      {...props}
      value={dataSources?.length ? dataSources : null}
      disabled={false}
      componentRight={
        !disabled && (
          <div className="fgl-link" onClick={() => setShowModal(true)}>
            {!!dataSources && dataSources?.length > 0
              ? 'Edit Data Source'
              : 'Add Data Source'}
          </div>
        )
      }
    >
      <div className="data-source-container ">
        <DataSourceFieldCard
          key={data?.props?.dataSource?.id}
          parentField={dataSources}
          childField={childValues}
          handleRemove={handleRemove}
          disabled={disabled}
          data={data}
        />
        {!data?.props?.dataSource?.length && <div className="dashes">--</div>}
      </div>
      {showModal && (
        <DataSourceModal
          data={data}
          updater={updater}
          onClose={() => setShowModal(false)}
          onSave={() => {}}
          saveDsAndProjects={saveDsAndProjects}
        />
      )}
    </FieldGroup>
  );
};

interface DataSourceFieldCardProps {
  parentField: any;
  childField: any;
  handleRemove: (item) => {};
  data: Partial<Metadata>;
  disabled: boolean;
}
const DataSourceFieldCard = ({
  parentField,
  childField,
  handleRemove,
  disabled,
  data
}: DataSourceFieldCardProps) => {
  const parents = React.useMemo(() => {
    let parentArr = [];
    if (!!parentField) {
      parentField.map(dataSource => {
        parentArr.push(dataSource);
      });
      return parentArr;
    }
    return parentArr;
  }, [data, parentField]);

  const children = React.useMemo(() => {
    let childrenArr = [];
    if (!!childField) {
      childField.map(project => {
        childrenArr.push(project);
      });
      return childrenArr;
    }
    return childrenArr;
  }, [data, childField]);

  if (!parentField) return null;

  return (
    <div>
      <DataSourceFieldValue
        data={data}
        children={children}
        parents={parents}
        disabled={disabled}
        onRemove={handleRemove}
      />
    </div>
  );
};

const itemComparator = (a: Metadata, b: Metadata) => {
  const ta: string = (a?.props?.name || '').trim();
  const tb: string = (b?.props?.name || '').trim();
  if (ta == tb) {
    const ia = '' + (a?.props?.itemId || '');
    const ib = '' + (b?.props?.itemId || '');
    return ia.localeCompare(ib);
  }
  return ta.localeCompare(tb);
};

type DSFVProps = {
  data: Partial<Metadata>;
  children: Metadata[];
  parents: Metadata[];
  onRemove: (item) => {};
  disabled: boolean;
};

const DataSourceFieldValue = ({
  data,
  children,
  parents,
  disabled,
  onRemove
}: DSFVProps) => {
  const [removingRefId, setRemovingRefId] = React.useState<string>(null);
  const treeData = React.useMemo(() => {
    let treeData = [];
    let projects = [...children].sort(itemComparator);
    treeData = parents
      ?.map(ds => {
        let dataSourceProjects = [];
        projects.map(project => {
          if (ds?.props?.id == project?.props?.dataSourceId) {
            dataSourceProjects.push(project);
          }
        });
        return { ...ds, children: dataSourceProjects };
      })
      .sort((a, b) => itemComparator(a, b));
    return treeData;
  }, [data, parents, children]);
  const pushResponse = useSelector(s => s.form?.pushResponse);
  const vr: ValidationResponse = pushResponse;
  return (
    <TreeSelect
      disabled={true}
      getData={treeData}
      getChildren={i => (!i.children ? [] : i.children)}
      selected={[...parents, ...children]}
      getClassName={(n, t) => {
        const cns = [];
        if ((t != 'display' && t != 'implied') || !vr) return cns;
        const refIds = vr.errors?.reduce<string[]>(
          (ids, e) => ids.concat(e.refIds),
          []
        );
        traverse([n], node => {
          if (refIds?.includes(node.data?.props?.refId)) {
            cns.push('--error');
            return true;
          }
        });
        return cns;
      }}
      renderData={i => {
        if (!i?.data?.props) return null;
        const refId = i.data.props.refId;
        const projectId = i.data.props.projectId;
        const name = i.data?.props?.name;

        return (
          <div>
            <span className="data-source-type-text"></span>
            {projectId && (
              <>
                <b>{projectId}</b> -{' '}
              </>
            )}
            {name}
            {removingRefId == refId && (
              <Icon>
                <Spinner spinnerSize="icon" />
              </Icon>
            )}
            {!!onRemove && removingRefId != refId && !disabled && (
              <Icon
                onClick={async () => {
                  setRemovingRefId(refId);
                  await onRemove(i.data);
                  setRemovingRefId(null);
                }}
              >
                <Close />
              </Icon>
            )}
          </div>
        );
      }}
      getUniqueKey={i => i?.props?.refId}
      onChange={() => {}}
      isHidden={() => false}
      settings={[{}, {}, {}]}
    />
  );
};

const isDisabled = data => {
  const itemId = data?.props?.itemId;
  const statusId = getItemStatusId(data);
  const disabled =
    itemId ||
    statusId == ItemStatus.PendingCPPItemCreation ||
    isRemovedOrRetired(statusId) ||
    statusId == ItemStatus.Not_Pursued;
  return disabled;
};
